import { cellAt } from '../board/board';
import { cellsWithin } from '../board/geometry';
import type { Cell } from '../board/types';
import { canAfford, hasCastableSpell, knowsSpell, SPELL_COSTS } from './spellCosts';
import { summonedElemental } from './elementals';
import { factionAffinity, isAtFavourablePeak, shiftTime } from './luminosity';
import { pieceAt, pieceById } from './movement';
import { newPiece } from './piece';
import { AFFLICTION_ROUNDS, ALL_SPELLS, NEUTRAL_SENTENCE } from './types';
import type { GameContext, GameState, Move, PendingBattle, Piece, SpellId } from './types';

/**
 * The active side's leading mage, where something needs one piece rather than all of them: Revive
 * places its piece around a mage, and the AI values a summon by the side that holds one.
 */
export function activeSpellcaster(ctx: GameContext, state: GameState): Piece | undefined {
  return state.pieces.find(
    (p) =>
      p.faction === state.activeFaction && !p.imprisoned && ctx.roster[p.character]?.spellcaster,
  );
}

/** Every piece of the active side that could cast something this turn. */
export function activeCasters(ctx: GameContext, state: GameState): Piece[] {
  return state.pieces.filter(
    (p) =>
      p.faction === state.activeFaction &&
      !p.imprisoned &&
      p.frozen === 0 &&
      !onPowerPoint(ctx, p) &&
      hasCastableSpell(ctx.roster[p.character], p),
  );
}

/** What a piece may cast right now: what it knows, and of that what it can pay for. */
export function spellsOf(
  ctx: GameContext,
  piece: Piece,
): { spell: SpellId; known: boolean; affordable: boolean }[] {
  const character = ctx.roster[piece.character];
  return ALL_SPELLS.map((spell) => ({
    spell,
    known: knowsSpell(character, spell),
    affordable: canAfford(piece, spell),
  }));
}

/**
 * Whether this piece could cast this spell at all, before anything is aimed at. A magician
 * standing on a circle cannot: a circle is where magic is gathered, and gathering it and spending
 * it are the two things a turn has to choose between. Shift Time is nobody's spell and never
 * reaches here.
 */
function casts(ctx: GameContext, state: GameState, caster: Piece, spell: SpellId): boolean {
  return (
    caster.faction === state.activeFaction &&
    !caster.imprisoned &&
    caster.frozen === 0 &&
    !onPowerPoint(ctx, caster) &&
    knowsSpell(ctx.roster[caster.character], spell) &&
    canAfford(caster, spell)
  );
}

/**
 * An enemy piece Curse or Freeze may be laid on: off a power point, whose spell immunity every
 * other hostile spell already honoured (core-rules:quality#7), and not already held by the same
 * spell.
 */
function canBeAfflicted(
  ctx: GameContext,
  state: GameState,
  p: Piece,
  spell: 'curse' | 'freeze',
): boolean {
  if (p.faction === state.activeFaction || p.imprisoned || onPowerPoint(ctx, p)) return false;
  return spell === 'curse' ? p.cursed === 0 : p.frozen === 0;
}

/** One of your own, held by Imprison or Freeze, that Release can free. */
function canBeReleased(state: GameState, p: Piece): boolean {
  return p.faction === state.activeFaction && (p.imprisoned || p.frozen > 0);
}

function onPowerPoint(ctx: GameContext, p: Piece): boolean {
  return cellAt(ctx.board, p.x, p.y)?.powerPoint ?? false;
}

/** Empty floor cells that are not power points – valid teleport and revive destinations. */
function isOpenCell(ctx: GameContext, state: GameState, x: number, y: number): boolean {
  const cell = cellAt(ctx.board, x, y);
  return !!cell && cell.kind === 'floor' && !cell.powerPoint && !pieceAt(state, x, y);
}

function canBeTeleported(ctx: GameContext, state: GameState, p: Piece): boolean {
  return p.faction === state.activeFaction && !p.imprisoned && !onPowerPoint(ctx, p);
}

function canBeHealed(ctx: GameContext, state: GameState, p: Piece): boolean {
  return p.faction === state.activeFaction && p.damage > 0 && !onPowerPoint(ctx, p);
}

function canBeExchanged(ctx: GameContext, p: Piece): boolean {
  return !p.imprisoned && !onPowerPoint(ctx, p);
}

function canBeImprisoned(ctx: GameContext, state: GameState, p: Piece): boolean {
  return (
    p.faction !== state.activeFaction &&
    !p.imprisoned &&
    !onPowerPoint(ctx, p) &&
    !isAtFavourablePeak(ctx, state, p)
  );
}

/** Cells around the mage (touching it, corners included on squares) where a revived piece may appear. */
export function reviveCells(ctx: GameContext, state: GameState, mage: Piece): Cell[] {
  return cellsWithin(ctx.board, mage.x, mage.y, 1).filter((c) => isOpenCell(ctx, state, c.x, c.y));
}

/**
 * Cells touching an enemy (corners included on squares, like Revive) where a summoned elemental
 * may appear to attack it. Power points are never spell destinations, so they are left out here
 * too.
 */
export function summonCells(ctx: GameContext, state: GameState, target: Piece): Cell[] {
  return cellsWithin(ctx.board, target.x, target.y, 1).filter((c) =>
    isOpenCell(ctx, state, c.x, c.y),
  );
}

/** An enemy a summoned elemental may attack: off a power point (spell immunity) and reachable. */
function canBeSummonedAgainst(ctx: GameContext, state: GameState, p: Piece): boolean {
  return (
    p.faction !== state.activeFaction &&
    !onPowerPoint(ctx, p) &&
    summonCells(ctx, state, p).length > 0
  );
}

/** All spells the active side may cast this turn, with every caster and every legal target. */
export function spellMoves(ctx: GameContext, state: GameState): Move[] {
  const moves: Move[] = [];
  // Letting the round pass belongs to the player, not to a piece, so it needs no caster and costs
  // nothing. It is the one entry here that is always available.
  moves.push({ type: 'spell', spell: 'shift-time' });

  const openCells = ctx.board.cells.filter((c) => isOpenCell(ctx, state, c.x, c.y));
  const graveyard = state.factions.find((f) => f.id === state.activeFaction)?.graveyard ?? [];
  const mage = activeSpellcaster(ctx, state);

  for (const caster of activeCasters(ctx, state)) {
    const casterId = caster.id;
    for (const spell of ctx.roster[caster.character]?.spells ?? []) {
      if (!canAfford(caster, spell)) continue;
      switch (spell) {
        case 'teleport':
          for (const p of state.pieces) {
            if (!canBeTeleported(ctx, state, p)) continue;
            for (const c of openCells)
              moves.push({ type: 'spell', spell, casterId, pieceId: p.id, x: c.x, y: c.y });
          }
          break;
        case 'heal':
          for (const p of state.pieces)
            if (canBeHealed(ctx, state, p))
              moves.push({ type: 'spell', spell, casterId, pieceId: p.id });
          break;
        case 'exchange': {
          const movable = state.pieces.filter((p) => canBeExchanged(ctx, p));
          for (let i = 0; i < movable.length; i++)
            for (let j = i + 1; j < movable.length; j++)
              moves.push({
                type: 'spell',
                spell,
                casterId,
                pieceId: movable[i]!.id,
                otherId: movable[j]!.id,
              });
          break;
        }
        case 'revive':
          // A revived piece steps out beside a mage, so a lesser caster needs one on the board.
          if (!mage) break;
          for (const character of new Set(graveyard))
            for (const c of reviveCells(ctx, state, mage))
              moves.push({ type: 'spell', spell, casterId, character, x: c.x, y: c.y });
          break;
        case 'imprison':
          for (const p of state.pieces)
            if (canBeImprisoned(ctx, state, p))
              moves.push({ type: 'spell', spell, casterId, pieceId: p.id });
          break;
        case 'curse':
        case 'freeze':
          for (const p of state.pieces)
            if (canBeAfflicted(ctx, state, p, spell))
              moves.push({ type: 'spell', spell, casterId, pieceId: p.id });
          break;
        case 'release':
          for (const p of state.pieces)
            if (canBeReleased(state, p))
              moves.push({ type: 'spell', spell, casterId, pieceId: p.id });
          break;
        case 'summon-elemental':
          if (!summonedElemental(ctx, state)) break;
          for (const p of state.pieces) {
            if (!canBeSummonedAgainst(ctx, state, p)) continue;
            for (const c of summonCells(ctx, state, p))
              moves.push({ type: 'spell', spell, casterId, pieceId: p.id, x: c.x, y: c.y });
          }
          break;
        case 'shift-time':
          break;
      }
    }
  }
  return moves;
}

/** Same rules as `spellMoves`, checked directly for one move (no enumeration). */
export function isLegalSpell(ctx: GameContext, state: GameState, move: Move): boolean {
  if (move.type !== 'spell') return false;
  // Letting the round pass is the player's to take and needs nobody to cast it.
  if (move.spell === 'shift-time') return true;
  const caster = pieceById(state, move.casterId);
  if (!caster || !casts(ctx, state, caster, move.spell)) return false;
  const mage = activeSpellcaster(ctx, state);
  switch (move.spell) {
    case 'teleport': {
      const p = pieceById(state, move.pieceId);
      return !!p && canBeTeleported(ctx, state, p) && isOpenCell(ctx, state, move.x, move.y);
    }
    case 'heal': {
      const p = pieceById(state, move.pieceId);
      return !!p && canBeHealed(ctx, state, p);
    }
    case 'exchange': {
      const a = pieceById(state, move.pieceId);
      const b = pieceById(state, move.otherId);
      return !!a && !!b && a.id !== b.id && canBeExchanged(ctx, a) && canBeExchanged(ctx, b);
    }
    case 'revive': {
      const graveyard = state.factions.find((f) => f.id === state.activeFaction)?.graveyard ?? [];
      return (
        !!mage &&
        graveyard.includes(move.character) &&
        reviveCells(ctx, state, mage).some((c) => c.x === move.x && c.y === move.y)
      );
    }
    case 'imprison': {
      const p = pieceById(state, move.pieceId);
      return !!p && canBeImprisoned(ctx, state, p);
    }
    case 'curse':
    case 'freeze': {
      const p = pieceById(state, move.pieceId);
      return !!p && canBeAfflicted(ctx, state, p, move.spell);
    }
    case 'release': {
      const p = pieceById(state, move.pieceId);
      return !!p && canBeReleased(state, p);
    }
    case 'summon-elemental': {
      const p = pieceById(state, move.pieceId);
      return (
        !!summonedElemental(ctx, state) &&
        !!p &&
        canBeSummonedAgainst(ctx, state, p) &&
        summonCells(ctx, state, p).some((c) => c.x === move.x && c.y === move.y)
      );
    }
  }
}

/**
 * Whether two moves are the same move: field by field, whatever order the keys were written in,
 * and with Exchange's two pieces in either order, since swapping them is one move. The server
 * checks a player's move against the rules' own list with this, and a player may pick the two
 * pieces of an Exchange in any order.
 */
export function sameMove(a: Move, b: Move): boolean {
  const x: Record<string, unknown> = canonicalMove(a);
  const y: Record<string, unknown> = canonicalMove(b);
  const keys = new Set([...Object.keys(x), ...Object.keys(y)]);
  for (const key of keys) if (x[key] !== y[key]) return false;
  return true;
}

function canonicalMove(move: Move): Move {
  if (move.type === 'spell' && move.spell === 'exchange' && move.otherId < move.pieceId)
    return { ...move, pieceId: move.otherId, otherId: move.pieceId };
  return move;
}

/**
 * Applies an already-validated spell. Summon Elemental leaves the game in the `battle` phase with
 * the elemental as attacker, exactly like a capture; every other spell leaves it in `play`.
 */
export function castSpell(
  ctx: GameContext,
  state: GameState,
  move: Move & { type: 'spell' },
): GameState {
  let pieces = state.pieces;
  let luminosity = state.luminosity;
  let nextPieceId = state.nextPieceId;
  let phase = state.phase;
  let pendingBattle: PendingBattle | null = state.pendingBattle;
  // Each spell cast wears the mage's protection down. Letting the round pass is not casting.
  const factions = state.factions.map((f) =>
    f.id === state.activeFaction && move.spell !== 'shift-time'
      ? { ...f, spellsCast: f.spellsCast + 1 }
      : f,
  );
  const active = factions.find((f) => f.id === state.activeFaction)!;
  // The caster pays for it. Shift Time is free and has nobody to charge.
  if (move.spell !== 'shift-time') {
    const cost = SPELL_COSTS[move.spell];
    pieces = pieces.map((p) =>
      p.id === move.casterId ? { ...p, spellPoints: Math.max(0, p.spellPoints - cost) } : p,
    );
  }

  switch (move.spell) {
    case 'teleport':
      pieces = pieces.map((p) => (p.id === move.pieceId ? { ...p, x: move.x, y: move.y } : p));
      break;
    case 'heal':
      pieces = pieces.map((p) => (p.id === move.pieceId ? { ...p, damage: 0 } : p));
      break;
    case 'shift-time':
      luminosity = shiftTime(luminosity);
      break;
    case 'exchange': {
      const a = pieceById(state, move.pieceId)!;
      const b = pieceById(state, move.otherId)!;
      pieces = pieces.map((p) =>
        p.id === a.id ? { ...p, x: b.x, y: b.y } : p.id === b.id ? { ...p, x: a.x, y: a.y } : p,
      );
      break;
    }
    case 'revive': {
      const index = active.graveyard.indexOf(move.character);
      active.graveyard = [
        ...active.graveyard.slice(0, index),
        ...active.graveyard.slice(index + 1),
      ];
      pieces = [
        ...pieces,
        newPiece(ctx.roster, nextPieceId, active.id, move.character, move.x, move.y),
      ];
      nextPieceId += 1;
      break;
    }
    case 'curse':
      pieces = pieces.map((p) => (p.id === move.pieceId ? { ...p, cursed: AFFLICTION_ROUNDS } : p));
      break;
    case 'freeze':
      pieces = pieces.map((p) => (p.id === move.pieceId ? { ...p, frozen: AFFLICTION_ROUNDS } : p));
      break;
    case 'release':
      // One spell frees either hold, so a side is never without an answer to the other's.
      pieces = pieces.map((p) =>
        p.id === move.pieceId
          ? { ...p, imprisoned: false, frozen: 0, ...(p.sentence ? { sentence: 0 } : {}) }
          : p,
      );
      break;
    case 'imprison': {
      // The cycle frees a Light or Dark prisoner at its peak; a neutral one serves a sentence.
      const neutral = factionAffinity(ctx, pieceById(state, move.pieceId)!.faction) === 'neutral';
      pieces = pieces.map((p) =>
        p.id === move.pieceId
          ? { ...p, imprisoned: true, ...(neutral ? { sentence: NEUTRAL_SENTENCE } : {}) }
          : p,
      );
      break;
    }
    case 'summon-elemental': {
      // The elemental fights for the caster's side; `applyBattleResult` removes it afterwards.
      const target = pieceById(state, move.pieceId)!;
      pieces = [
        ...pieces,
        newPiece(
          ctx.roster,
          nextPieceId,
          active.id,
          summonedElemental(ctx, state)!.id,
          move.x,
          move.y,
        ),
      ];
      phase = 'battle';
      pendingBattle = { attackerId: nextPieceId, defenderId: target.id, x: target.x, y: target.y };
      nextPieceId += 1;
      break;
    }
  }

  return { ...state, pieces, factions, luminosity, nextPieceId, phase, pendingBattle };
}
